import React from 'react';

import type { GamAdUnitProps } from '@client/adManager/AdUnit/GamAdUnit.js';
import { updatePlacementKeyValueById } from '@client/core/atoms/placements.js';
import { events, messageBus } from '@schibsted-nmp/advertising-events';
import { debugLog } from '@schibsted-nmp/advertising-shared';

import { useForceRerenderOnFilteringOrPaginating } from './useForceRerenderOnFilteringOrPaginating.js';

const LEGACY_EVENT_TOPIC = 'render-sponsored-content';

type Props = GamAdUnitProps & {
  children: React.ReactNode;
};

export function RenderSponsoredContentOrChildren(props: Props) {
  const { children: backFillAd, placement } = props;
  const { placementId } = placement;
  const [loadBackFill, setLoadBackFill] = React.useState(false);

  React.useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    let hasPlacementSpecificDecision = false;

    const eventTopic = `render-sponsored-content:${placementId}`;

    const handleContentDecision = (hasContent: boolean) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      setLoadBackFill(!hasContent);
      if (hasContent) {
        updatePlacementKeyValueById(placementId, 'status', 'loaded');
        updatePlacementKeyValueById(
          placementId,
          'creativeType',
          'sponsored-content'
        );
      } else {
        updatePlacementKeyValueById(placementId, 'status', 'pending');
        updatePlacementKeyValueById(placementId, 'creativeType', 'banner');
      }
    };

    const handlePlacementDecision = (hasContent: boolean) => {
      hasPlacementSpecificDecision = true;
      handleContentDecision(hasContent);
    };

    // TODO: Remove the legacy topic once contentful-sov-fetcher publishes the placement-specific topic everywhere.
    // The legacy topic is shared by all placements, so it can cross-contaminate decisions.
    const handleLegacyDecision = (hasContent: boolean) => {
      if (hasPlacementSpecificDecision) return;
      debugLog(
        `[RenderSponsoredContentOrChildren] Using legacy ${LEGACY_EVENT_TOPIC} for ${placementId}`
      );
      handleContentDecision(hasContent);
    };

    try {
      const peekEvent = messageBus.peek(events.PODLET.channel, eventTopic);
      const legacyPeekEvent = messageBus.peek(
        events.PODLET.channel,
        LEGACY_EVENT_TOPIC
      );

      if (peekEvent?.payload !== undefined) {
        handlePlacementDecision(Boolean(peekEvent.payload));
      } else if (legacyPeekEvent?.payload !== undefined) {
        handleLegacyDecision(Boolean(legacyPeekEvent.payload));
      } else {
        timeoutId = setTimeout(() => {
          handleContentDecision(false);
        }, 1000);
      }
    } catch (error) {
      debugLog(`Failed to peek on ${eventTopic}`, error);
    }

    const unsubscribePlacement = messageBus.subscribe(
      events.PODLET.channel,
      eventTopic,
      (event) => {
        handlePlacementDecision(Boolean(event.payload));
      }
    );
    const unsubscribeLegacy = messageBus.subscribe(
      events.PODLET.channel,
      LEGACY_EVENT_TOPIC,
      (event) => {
        handleLegacyDecision(Boolean(event.payload));
      }
    );

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      unsubscribePlacement();
      unsubscribeLegacy();
    };
  }, [placementId]);

  const renderKey = useForceRerenderOnFilteringOrPaginating();

  return (
    <React.Fragment key={`${placementId}$_${renderKey}`}>
      <contentful-sov-fetcher adPosition={placementId} />
      {/* contentful-sov-fetcher must still be returned alongside backFillAd; 
      omitting it prevents the next SOV query from being called upon filtering or pagination */}
      {loadBackFill && backFillAd}
    </React.Fragment>
  );
}
